import StorageManager from "../helpers/StorageManager";
import { getContextId, setContextId, readContextIdFromResponse, ensureContextId } from "../helpers/ContextId";

export default class BaseNewApiServiceV2 {
  private headers: HeadersInit;

  constructor() {
    this.headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
      "Cache-Control":
        "public, max-age=0, s-maxage=3600, stale-while-revalidate=600",
    };

    const token = StorageManager.get("token");
    if (token) {
      this.headers["Authorization"] = token;
    }
  }

  // Get base URL dynamically to support both server and client
  private get baseUrl(): string {
    // Browser requests should always go through Remix proxy route.
    if (typeof window !== "undefined") {
      return "/api/proxy";
    }
    // Fallback to process.env (server-side or default)
    return process.env.REMIX_APP_API_HOST_V2 || "https://apiv2dev.office-hub.com";
  }

  // --------------------------------------------
  // 🔹 Centralized Response Handler
  // --------------------------------------------
  private async handleResponse(response: Response) {
    // Sync context id from response headers (both success and error cases)
    const ctxId = readContextIdFromResponse(response);
    if (ctxId) setContextId(ctxId);
    if (response.status === 304) {
      return null;
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      // Only log errors in development
      if (typeof window === "undefined" || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        console.error("FROM BaseNewApiServiceV2");
        console.error("OH-ERROR", JSON.stringify(errorData, null, 2));
      }
      throw new Error(response.statusText);
    }

    return response.json();
  }

  // --------------------------------------------
  // 🔹 GET Method
  // --------------------------------------------
  async apiGet(path: string, link?: string) {
    let url = link ? link : `${this.baseUrl}${path}`;
    console.log("url", url);
    const headers: Record<string, string> = { ...(this.headers as Record<string, string>) };
    // ---------------------------------------------------------
    // SECURITY: Handle Secret (Server) vs Proxy (Client)
    // ---------------------------------------------------------
    if (typeof window === "undefined") {
      // SERVER SIDE: Inject Secret
      if (process.env.API_SECRET_KEY) {
        headers["x-api-secret"] = process.env.API_SECRET_KEY;
      } else {
        console.error("BaseApiServiceV2 (Server): API_SECRET_KEY is MISSING in process.env");
      }
    } else {
      // CLIENT SIDE: rewrite only absolute API URLs to the Remix proxy.
      // Do not match substrings in paths (e.g. verify/email ending in @office-hub.com).
      if (
        /^https?:\/\//i.test(url) &&
        (url.includes("office-hub.com") ||
          url.includes("localhost") ||
          url.includes("dev-v2.office-hub.com"))
      ) {
        const urlObj = new URL(url);
        url = `/api/proxy${urlObj.pathname}${urlObj.search}`;
      }
    }

    const existingCtx = ensureContextId();
    if (existingCtx) headers["X-Context-Id"] = existingCtx;

    const response = await fetch(url, {
      method: "GET",
      headers,
    });

    return this.handleResponse(response);
  }

  // --------------------------------------------
  // 🔹 POST Method (invalidates cache if needed)
  // --------------------------------------------
  async apiPost(path: string, data?: any, link?: string) {
    const url = link ? link : `${this.baseUrl}${path}`;
    const headers: Record<string, string> = { ...(this.headers as Record<string, string>) };
    const existingCtx = ensureContextId();
    if (existingCtx) headers["X-Context-Id"] = existingCtx;
    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    });

    const result = await this.handleResponse(response);
    return result;
  }
}
